import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import {
	AutoComplete,
	Col,
	DatePicker,
	Form,
	Input,
	Row,
	Select,
	Spin,
	Typography,
} from "antd";
import { debounce, isString } from "lodash";
import { formLayout, formRules } from "../../../../../utilities";
import { withRouter } from "react-router-dom";
import WizardFormButtons from "../../../../ui/forms/WizardFormButtons";
import moment from "moment";
import PropTypes from "prop-types";

function disabledDate(current) {
	// Can not select days before today
	return current && current < moment().startOf('day');
}

/**
 * Page of the client data form wizard with basic user details.
 */
@inject("context")
@withRouter
@observer
class BasicDetails extends Component {
	formRef = React.createRef();
	static propTypes = {
		service: PropTypes.object.isRequired,
	};

	getInitialDate() {
		const { clientData } = this.props.service;
		const path = this.props.location.pathname;
		if (path.includes("renew")) {
			return clientData.startDate ? moment(clientData.startDate) : null;
		} else if (path.includes("mta")) {
			return clientData.startDate ? moment(clientData.startDate) : null;
		}
		return moment(); // Default date is today if not renewal or mta
	}

	getLabel() {
		const path = this.props.location.pathname;
		if (path.includes("renew")) {
			return "Renewal date";
		} else if (path.includes("mta")) {
			return "MTA Start date";
		} else {
			return "Start Date";
		}
	}

	render() {
		const { postcodeLookupService } = this.props.context;
		const { isViewMode } = this.props.context.policiesService;
		let postcodeAutocompleteOptions;

		if (!!postcodeLookupService.postcodeData.addresses) {
			postcodeAutocompleteOptions = postcodeLookupService.postcodeData.addresses.map(
				(addr, index) => {
					let address = addr.formatted_address.filter((el) => !!el).join(", ");
					return {
						label: address,
						value: index.toString(),
					};
				}
			);
		}

		const { clientData } = this.props.service;
		const phoneService = this.props.context.insuranceQuotingService;

		// generate key for the form to force re-render
		let key = 0;
		if (clientData.organisation) {
			key = clientData.organisation.organisationName;
		}

		return (
			<Form
				{...formLayout}
				onFinish={this.props.onFinish}
				colon={false}
				ref={this.formRef}
				key={key}
				initialValues={{
					organisation: {
						...clientData.organisation,
						organisationFirstName:
							clientData.organisation?.organisationFirstName,
						organisationLastName: clientData.organisation?.organisationLastName,
						organisationName: clientData.organisation?.organisationName,
						organisationType: clientData.organisation?.organisationType,
						phone: clientData.organisation?.phone,
						emailAddress: clientData.organisation?.emailAddress,
						address: {
							...clientData.organisation?.address,
							address1: clientData.organisation?.address?.address1,
							address2: clientData.organisation?.address?.address2,
							address3: clientData.organisation?.address?.address3,
							city: clientData.organisation?.address?.address4,
							flatNo: clientData.organisation?.address?.flatNo,
							postcode:
								clientData.organisation?.address?.postcode ||
								clientData.organisation?.address?.post_code,
							post_code:
								clientData.organisation?.address?.post_code ||
								clientData.organisation?.address?.postcode,
						},
					},
					startDate: this.getInitialDate(),
				}}
			>
				<Row>
					<Col lg={{ span: 8 }}>
						<Typography.Title level={4}>Applicant details</Typography.Title>
					</Col>
				</Row>
				<Row>
					<Col md={12}>
						<Form.Item
							name={["organisation", "organisationName"]}
							label="Company name if applicable"
						>
							<Input disabled={isViewMode} placeholder="Company name" />
						</Form.Item>
					</Col>
					<Col md={8}>
						<Form.Item
							name="startDate"
							label={this.getLabel()}
							format="dateFormat"
							rules={[formRules.required]}
						>
							<DatePicker disabled={isViewMode} format="DD-MM-YYYY" disabledDate={disabledDate} />
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col md={12}>
						<Form.Item
							label="Organisation Type"
							name={["organisation", "organisationType"]}
						>
							<Select disabled={isViewMode}>
								<Select.Option value="Individual">Individual</Select.Option>
								<Select.Option value="Sole Trader">Sole Trader</Select.Option>
								<Select.Option value="Trustee">Trustee</Select.Option>
								<Select.Option value="CIC">CIC</Select.Option>
								<Select.Option value="Club">Club</Select.Option>
								<Select.Option value="Charity">Charity</Select.Option>
								<Select.Option value="Plc">Plc</Select.Option>
								<Select.Option value="Ltd. Co.">Ltd. Co.</Select.Option>
								<Select.Option value="Partnership">Partnership</Select.Option>
								<Select.Option value="Other">Other</Select.Option>
							</Select>
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col md={12} lg={{ span: 12 }}>
						<Form.Item
							name={["organisation", "organisationFirstName"]}
							label="First name"
							rules={[formRules.required]}
						>
							<Input
								disabled={isViewMode}
								placeholder="Your first name"
								style={{ textTransform: "capitalize" }}
							/>
						</Form.Item>
					</Col>
					<Col md={12} lg={8}>
						<Form.Item
							name={["organisation", "organisationLastName"]}
							label="Last name"
							rules={[formRules.required]}
						>
							<Input
								disabled={isViewMode}
								placeholder="Your last name"
								style={{ textTransform: "capitalize" }}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col md={12} lg={{ span: 12 }}>
						<Form.Item
							name={["organisation", "phone"]}
							label="Telephone"
						>
							<Input
								disabled={isViewMode}
								placeholder="Mobile or Landline"
								onChange={(e) => this.props.checkUKTelephone(e.target.value)}
							/>
						</Form.Item>
						<label
							style={{
								float: "right",
								marginTop: -20,
								color:
									phoneService.phoneNumberMessage ===
										"Valid UK telephone number"
										? "white"
										: "#ff4d4f",
							}}
						>
							{phoneService.phoneNumberMessage}
						</label>
					</Col>
					<Col md={12} lg={8}>
						<Form.Item
							name={["organisation", "emailAddress"]}
							label="Email"
							rules={[formRules.email, formRules.required]}
						>
							<Input disabled={isViewMode} placeholder="Email" />
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col lg={{ span: 8 }}>
						<Typography.Title level={4}>Address </Typography.Title>
						<p>Please provide your address for correspondence</p>
					</Col>
				</Row>
				<Row>
					<Col lg={{ span: 12, offset: 0 }} >
						<Form.Item
							name={["organisation", "address", "postcode"]}
							label="Postcode"
							help={
								postcodeLookupService.postcodeLookupError ||
								"Enter a valid UK postcode — address suggestions load automatically."
							}
							validateStatus={
								postcodeLookupService.postcodeLookupError ? "warning" : undefined
							}
						>
							<AutoComplete
								disabled={isViewMode}
								size="medium"
								style={{ width: "100%" }}
								dropdownMatchSelectWidth={false}
								options={postcodeAutocompleteOptions}
								onSelect={this.handleAutocompleteAddressSelected}
								onSearch={this.handleAutocompleteSearch}
								placeholder="Postcode"
								notFoundContent={
									postcodeLookupService.postcodeLookupLoading ? (
										<Spin size="small" />
									) : (
										<span>No matches — continue typing a full UK postcode</span>
									)
								}
								filterOption={false}
							/>
						</Form.Item>
						<Form.Item name={["organisation", "address", "post_code"]} hidden>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col lg={{ span: 12 }}>
						<Form.Item
							name={["organisation", "address", "flatNo"]}
							label="House/Flat number"
						>
							<Input disabled={isViewMode} placeholder="House/Flat number" />
						</Form.Item>
						<Form.Item
							name={["organisation", "address", "address1"]}
							label="House Number and Street "
						>
							<Input disabled={isViewMode} placeholder="House Number and Street" />
						</Form.Item>
						<Form.Item
							name={["organisation", "address", "address2"]}
							label="Address Line 2"
						>
							<Input disabled={isViewMode} placeholder="Address Line 2" />
						</Form.Item>
						<Form.Item
							name={["organisation", "address", "address3"]}
							label="Address Line 3"
						>
							<Input disabled={isViewMode} placeholder="Address Line 3" />
						</Form.Item>
						<Form.Item
							name={["organisation", "address", "address4"]}
							label="City/Town"
						>
							<Input disabled={isViewMode} placeholder="City or Town" />
						</Form.Item>
						<Form.Item
							name={["organisation", "address", "address5"]}
							label="County"
						>
							<Input disabled={isViewMode} placeholder="County" />
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col xs={24} className="d-flex">
						<WizardFormButtons tabs={this.props.tabs} />
					</Col>
				</Row>
			</Form>
		);
	}

	handleAutocompleteAddressSelected = (value) => {
		const { resetFields, setFieldsValue } = this.formRef.current;
		const { postcodeLookupService } = this.props.context;
		const address = postcodeLookupService.postcodeData.addresses[value];

		resetFields([
			["organisation", "address", "flatNo"],
			["organisation", "address", "address1"],
			["organisation", "address", "address2"],
			["organisation", "address", "address3"],
			["organisation", "address", "address4"],
			["organisation", "address", "address5"],
			["organisation", "address", "post_code"],
		]);

		const flatNumber = [address.sub_building_name, address.building_name]
			.filter((el) => !!el)
			.join(" / ");

		const address1 = [address.formatted_address[0]]
			.filter((el) => !!el)
			.join(", ");

		const address2 = [address.formatted_address[1]]
			.filter((el) => !!el)
			.join(", ");

		const address3 = [address.formatted_address[2]]
			.filter((el) => !!el)
			.join(", ");

		const address4 = [address.formatted_address[3]]
			.filter((el) => !!el)
			.join(", ");

		const pc = postcodeLookupService.postcodeData.postcode;

		setFieldsValue({
			organisation: {
				address: {
					address4,
					address1,
					address2,
					flatNo: flatNumber,
					address3,
					address5: address.address5,
					postcode: pc,
					post_code: pc,
				},
			},
		});
	};

	handleAutocompleteSearch = debounce((value) => {
		const { postcodeLookupService } = this.props.context;

		if (
			isString(value) &&
			isString(postcodeLookupService.postcodeData.postcode) &&
			value.trim().toLowerCase() !==
			postcodeLookupService.postcodeData.postcode.toLowerCase()
		) {
			postcodeLookupService.setPostcodeData({});
		}

		postcodeLookupService.loadPostcodeData(value);
	}, 250);
}

export default BasicDetails;
